import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ActionMenu, type ActionMenuEntry } from "@/components/admin/action-menu";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { MarkdownEditor } from "@/components/admin/markdown-editor";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import {
	type Article,
	type ArticleDetail,
	type ArticlePayload,
	BLOG_STATUS_LABELS,
	type BlogStatus,
	blogApi,
} from "@/lib/marketing/blog-api";

const CLE = ["marketing", "blog"] as const;

const nombre = (n: number) => n.toLocaleString("fr-FR");

const dateCourte = (iso: string | null) =>
	iso
		? new Date(iso).toLocaleDateString("fr-FR", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			})
		: "—";

const ETAPES: Array<{ type: string; label: string }> = [
	{ type: "created", label: "Brouillon créé" },
	{ type: "submitted", label: "En relecture" },
	{ type: "published", label: "Publié" },
];

type Filtre = "all" | BlogStatus;

/**
 * Le Mag Akwaba — articles de la vitrine.
 *
 * La liste et l'éditeur sont deux écrans, pas un : un article se rédige dans
 * une fenêtre où l'on voit le texte entier, pas dans une colonne coincée à
 * côté d'un tableau.
 */
export function AdminMarketingMagPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny } = usePermissions();
	const peutEcrire = hasAny("MARKETING.CMS.WRITE");

	const [filtre, setFiltre] = useState<Filtre>("all");
	const [recherche, setRecherche] = useState("");
	const [edite, setEdite] = useState<string | null>(null);
	const [creation, setCreation] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "list", filtre, recherche],
		queryFn: () =>
			blogApi.list({
				status: filtre === "all" ? undefined : filtre,
				search: recherche.trim() || undefined,
				pageSize: 50,
			}),
	});
	const { data: stats } = useQuery({
		queryKey: [...CLE, "stats"],
		queryFn: () => blogApi.stats(),
	});

	const rafraichir = () => {
		void qc.invalidateQueries({ queryKey: CLE });
	};
	const echec = (e: unknown) => toast.error(extractErrorMessage(e));

	const creer = useMutation({
		mutationFn: (p: ArticlePayload & { title: string }) => blogApi.create(p),
		onSuccess: (a) => {
			toast.success("Brouillon créé");
			setCreation(false);
			setEdite(a.id);
			rafraichir();
		},
		onError: echec,
	});

	const publier = useMutation({
		mutationFn: ({ id, unpublish }: { id: string; unpublish?: boolean }) =>
			blogApi.publish(id, { unpublish }),
		onSuccess: (_d, v) => {
			toast.success(v.unpublish ? "Article dépublié" : "Article publié");
			rafraichir();
		},
		onError: echec,
	});

	const soumettre = useMutation({
		mutationFn: (id: string) => blogApi.submit(id),
		onSuccess: () => {
			toast.success("Article envoyé en relecture");
			rafraichir();
		},
		onError: echec,
	});

	const dupliquer = useMutation({
		mutationFn: (id: string) => blogApi.duplicate(id),
		onSuccess: () => {
			toast.success("Article dupliqué en brouillon");
			rafraichir();
		},
		onError: echec,
	});

	const archiver = useMutation({
		mutationFn: (id: string) => blogApi.archive(id),
		onSuccess: () => {
			toast.success("Article archivé");
			rafraichir();
		},
		onError: echec,
	});

	const menu = (a: Article): ActionMenuEntry[] => {
		const entrees: ActionMenuEntry[] = [
			{
				label: "Ouvrir l'éditeur",
				icon: "edit",
				onClick: () => setEdite(a.id),
			},
			{
				label: "Dupliquer",
				icon: "copy",
				disabled: !peutEcrire,
				onClick: () => dupliquer.mutate(a.id),
			},
		];

		if (a.status === "draft") {
			entrees.push({
				label: "Envoyer en relecture",
				icon: "check",
				disabled: !peutEcrire,
				onClick: () => soumettre.mutate(a.id),
			});
		}
		if (a.status !== "published") {
			entrees.push({
				label: "Publier",
				icon: "check",
				disabled: !peutEcrire,
				onClick: () => publier.mutate({ id: a.id }),
			});
		} else {
			entrees.push({
				label: "Dépublier",
				icon: "eyeOff",
				disabled: !peutEcrire,
				onClick: () => publier.mutate({ id: a.id, unpublish: true }),
			});
		}

		entrees.push("sep", {
			label: "Archiver",
			icon: "trash",
			danger: true,
			disabled: !peutEcrire,
			onClick: () => {
				void confirm({
					title: "Archiver cet article ?",
					message: `« ${a.title} » quittera la vitrine. Son adresse continuera d'exister et ses vues restent comptées.`,
					confirmLabel: "Archiver",
					tone: "danger",
				}).then((ok) => {
					if (ok) archiver.mutate(a.id);
				});
			},
		});

		return entrees;
	};

	const articles = data?.items ?? [];

	return (
		<>
			<PageHead
				title="Le Mag Akwaba"
				sub="Articles de la vitrine, relecture et publication"
				actions={
					peutEcrire && (
						<AdminButton variant="primary" onClick={() => setCreation(true)}>
							<AdminIcon n="plus" s={14} />
							Nouvel article
						</AdminButton>
					)
				}
			/>

			{stats && (
				<div className="kpi-grid">
					<div className="kpi forest">
						<div className="kpi-l">Publiés</div>
						<div className="kpi-v tn">{stats.published}</div>
						<span className="kpi-delta forest">
							{nombre(stats.totalViews)} vues
						</span>
					</div>
					<div className="kpi ochre">
						<div className="kpi-l">En relecture</div>
						<div className="kpi-v tn">{stats.review}</div>
					</div>
					<div className="kpi">
						<div className="kpi-l">Brouillons</div>
						<div className="kpi-v tn">{stats.draft}</div>
					</div>
				</div>
			)}

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 12,
					marginBottom: 14,
					flexWrap: "wrap",
				}}
			>
				<Segmented
					value={filtre}
					onChange={setFiltre}
					options={[
						{ value: "all", label: "Tous" },
						{ value: "published", label: "Publiés" },
						{ value: "review", label: "Relecture" },
						{ value: "draft", label: "Brouillons" },
						{ value: "archived", label: "Archivés" },
					]}
				/>
				<input
					className="aform-i"
					style={{ maxWidth: 260 }}
					placeholder="Rechercher un titre…"
					value={recherche}
					onChange={(e) => setRecherche(e.target.value)}
				/>
			</div>

			<Card>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Article</th>
								<th>Auteur</th>
								<th>Rubrique</th>
								<th>Statut</th>
								<th className="r">Vues</th>
								<th className="r">Partages</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={7}>
										<div className="empty">Chargement…</div>
									</td>
								</tr>
							)}
							{!isLoading && articles.length === 0 && (
								<tr>
									<td colSpan={7}>
										<div className="empty">
											Aucun article. Le premier donnera au site de quoi être
											trouvé sur autre chose que des noms de produits.
										</div>
									</td>
								</tr>
							)}
							{articles.map((a) => {
								const st = BLOG_STATUS_LABELS[a.status];
								return (
									<tr key={a.id}>
										<td>
											<button
												type="button"
												onClick={() => setEdite(a.id)}
												style={{
													background: "none",
													border: "none",
													padding: 0,
													textAlign: "left",
													cursor: "pointer",
													fontFamily: "inherit",
												}}
											>
												<strong style={{ fontSize: 12.5 }}>{a.title}</strong>
											</button>
											<div className="muted" style={{ fontSize: 11 }}>
												/mag/{a.slug} · {a.readingMinutes} min
											</div>
										</td>
										<td>{a.author ?? "—"}</td>
										<td>
											{a.category ? (
												<Tag variant="neutral" noDot>
													{a.category}
												</Tag>
											) : (
												<span className="muted">—</span>
											)}
										</td>
										<td>
											<Tag variant={st.variant}>{st.label}</Tag>
											{a.scheduledAt && a.status === "review" && (
												<div className="muted" style={{ fontSize: 10.5 }}>
													Prévu {dateCourte(a.scheduledAt)}
												</div>
											)}
										</td>
										<td className="r tn">{a.views ? nombre(a.views) : "—"}</td>
										<td className="r tn">{a.shares || "—"}</td>
										<td className="right">
											<ActionMenu items={menu(a)} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>

			{creation && (
				<CreationModal
					saving={creer.isPending}
					onClose={() => setCreation(false)}
					onSubmit={(p) => creer.mutate(p)}
				/>
			)}

			{edite && (
				<EditeurModal
					id={edite}
					readOnly={!peutEcrire}
					onClose={() => {
						setEdite(null);
						rafraichir();
					}}
				/>
			)}
		</>
	);
}

/** Création : juste de quoi démarrer, le reste se remplit dans l'éditeur. */
function CreationModal({
	onClose,
	onSubmit,
	saving,
}: {
	onClose: () => void;
	onSubmit: (p: ArticlePayload & { title: string }) => void;
	saving: boolean;
}) {
	const [titre, setTitre] = useState("");
	const [rubrique, setRubrique] = useState("");
	const [chapo, setChapo] = useState("");

	return (
		<Modal
			title="Nouvel article"
			subtitle="Le titre et la rubrique suffisent pour commencer"
			onClose={onClose}
			width={520}
		>
			<FormRow label="Titre" required>
				<input
					className="aform-i"
					value={titre}
					onChange={(e) => setTitre(e.target.value)}
					placeholder="Bien choisir son smartphone en 2026"
				/>
			</FormRow>
			<FormRow label="Rubrique">
				<input
					className="aform-i"
					value={rubrique}
					onChange={(e) => setRubrique(e.target.value)}
					placeholder="Tech"
				/>
			</FormRow>
			<FormRow
				label="Chapô"
				helper="Le paragraphe d'accroche affiché en liste"
			>
				<textarea
					className="aform-i"
					rows={3}
					value={chapo}
					onChange={(e) => setChapo(e.target.value)}
				/>
			</FormRow>
			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={titre.trim().length < 3 || saving}
					onClick={() =>
						onSubmit({
							title: titre.trim(),
							category: rubrique.trim() || null,
							excerpt: chapo.trim() || null,
						})
					}
				>
					Créer le brouillon
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}

/**
 * Éditeur d'un article : corps à gauche, réglages et frise de workflow à
 * droite — la disposition du design.
 *
 * L'enregistrement est explicite et non automatique : une sauvegarde
 * silencieuse sur un article publié changerait la vitrine sans que personne
 * ne l'ait demandé.
 */
function EditeurModal({
	id,
	onClose,
	readOnly,
}: {
	id: string;
	onClose: () => void;
	readOnly: boolean;
}) {
	const qc = useQueryClient();
	const toast = useToast();
	const { data } = useQuery({
		queryKey: [...CLE, "detail", id],
		queryFn: () => blogApi.detail(id),
	});

	const [brouillon, setBrouillon] = useState<Partial<ArticleDetail> | null>(null);
	useEffect(() => {
		if (data && !brouillon) setBrouillon(data);
	}, [data, brouillon]);

	const enregistrer = useMutation({
		mutationFn: (p: ArticlePayload) => blogApi.update(id, p),
		onSuccess: () => {
			toast.success("Article enregistré");
			void qc.invalidateQueries({ queryKey: CLE });
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const publier = useMutation({
		mutationFn: () => blogApi.publish(id, {}),
		onSuccess: () => {
			toast.success("Article publié");
			void qc.invalidateQueries({ queryKey: CLE });
			onClose();
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const soumettre = useMutation({
		mutationFn: () => blogApi.submit(id),
		onSuccess: () => {
			toast.success("Envoyé en relecture");
			void qc.invalidateQueries({ queryKey: CLE });
			onClose();
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const maj = <K extends keyof ArticleDetail>(k: K, v: ArticleDetail[K]) =>
		setBrouillon((etat) => ({ ...(etat ?? {}), [k]: v }));

	if (!data || !brouillon) {
		return (
			<Modal title="Éditeur d'article" onClose={onClose} width={960}>
				<div className="empty">Chargement…</div>
			</Modal>
		);
	}

	const atteinte = (type: string) =>
		data.events.some((e) => e.type === type) ||
		(type === "published" && data.status === "published");

	return (
		<Modal
			title={data.title}
			subtitle={`/mag/${data.slug} · ${data.readingMinutes} min de lecture`}
			onClose={onClose}
			width={1040}
		>
			<div className="cms-editor-grid">
				<div>
					<FormRow label="Titre" required>
						<input
							className="aform-i"
							disabled={readOnly}
							value={brouillon.title ?? ""}
							onChange={(e) => maj("title", e.target.value)}
							style={{
								fontFamily: "var(--a-font-display)",
								fontWeight: 700,
								fontSize: 16,
							}}
						/>
					</FormRow>
					<FormRow label="Chapô" helper="Affiché en liste et en tête d'article">
						<textarea
							className="aform-i"
							rows={2}
							disabled={readOnly}
							value={brouillon.excerpt ?? ""}
							onChange={(e) => maj("excerpt", e.target.value)}
						/>
					</FormRow>
					<FormRow label="Corps de l'article">
						<MarkdownEditor
							value={brouillon.body ?? ""}
							onChange={(v) => {
								if (!readOnly) maj("body", v);
							}}
							rows={16}
							placeholder="Rédigez l'article en Markdown : ## pour un sous-titre, **gras**, [lien](adresse)."
						/>
					</FormRow>
				</div>

				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<Card>
						<CardHead title="Réglages" />
						<CardBody>
							<FormRow label="Adresse" helper="Après /mag/">
								<input
									className="aform-i"
									disabled={readOnly}
									value={brouillon.slug ?? ""}
									onChange={(e) => maj("slug", e.target.value)}
								/>
							</FormRow>
							<FormRow label="Rubrique">
								<input
									className="aform-i"
									disabled={readOnly}
									value={brouillon.category ?? ""}
									onChange={(e) => maj("category", e.target.value)}
								/>
							</FormRow>
							<FormRow label="Auteur affiché">
								<input
									className="aform-i"
									disabled={readOnly}
									value={brouillon.author ?? ""}
									onChange={(e) => maj("author", e.target.value)}
								/>
							</FormRow>
							<FormRow label="Mots-clés" helper="Séparés par des virgules">
								<input
									className="aform-i"
									disabled={readOnly}
									value={(brouillon.tags ?? []).join(", ")}
									onChange={(e) =>
										maj(
											"tags",
											e.target.value
												.split(",")
												.map((t) => t.trim())
												.filter(Boolean),
										)
									}
								/>
							</FormRow>
							<div className="info-row">
								<span className="l">Vues</span>
								<span className="v tn">{nombre(data.views)}</span>
							</div>
							<div className="info-row" style={{ borderBottom: "none" }}>
								<span className="l">Partages</span>
								<span className="v tn">{nombre(data.shares)}</span>
							</div>
						</CardBody>
					</Card>

					<Card>
						<CardHead title="Référencement" />
						<CardBody>
							<FormRow label="Titre pour les moteurs" helper="60 car. max">
								<input
									className="aform-i"
									disabled={readOnly}
									value={brouillon.metaTitle ?? ""}
									onChange={(e) => maj("metaTitle", e.target.value)}
									placeholder={data.title}
								/>
							</FormRow>
							<FormRow label="Description" helper="155 car. max">
								<textarea
									className="aform-i"
									rows={3}
									disabled={readOnly}
									value={brouillon.metaDescription ?? ""}
									onChange={(e) => maj("metaDescription", e.target.value)}
									placeholder={data.excerpt ?? ""}
								/>
							</FormRow>
						</CardBody>
					</Card>

					<Card>
						<CardHead title="Avancement" />
						<CardBody>
							<div className="timeline-l">
								{ETAPES.map((etape) => {
									const fait = atteinte(etape.type);
									const courant =
										(etape.type === "submitted" && data.status === "review") ||
										(etape.type === "published" &&
											data.status === "published") ||
										(etape.type === "created" && data.status === "draft");
									const evenement = [...data.events]
										.reverse()
										.find((e) => e.type === etape.type);
									return (
										<div
											key={etape.type}
											className={`ev${courant ? " active" : fait ? " done" : ""}`}
										>
											<div className="t">{etape.label}</div>
											<div className="ts">
												{evenement
													? `${evenement.actorLabel ?? "—"} · ${dateCourte(evenement.createdAt)}`
													: "En attente"}
											</div>
										</div>
									);
								})}
							</div>
						</CardBody>
					</Card>
				</div>
			</div>

			<ModalFooter>
				<span
					className="muted"
					style={{ fontSize: 11, marginRight: "auto", alignSelf: "center" }}
				>
					Dernière modification {dateCourte(data.updatedAt)}
				</span>
				<AdminButton variant="ghost" onClick={onClose}>
					Fermer
				</AdminButton>
				{!readOnly && (
					<>
						<AdminButton
							variant="secondary"
							disabled={enregistrer.isPending}
							onClick={() =>
								enregistrer.mutate({
									title: brouillon.title,
									slug: brouillon.slug,
									excerpt: brouillon.excerpt,
									body: brouillon.body,
									category: brouillon.category,
									tags: brouillon.tags,
									authorLabel: brouillon.author ?? undefined,
									metaTitle: brouillon.metaTitle,
									metaDescription: brouillon.metaDescription,
								})
							}
						>
							Enregistrer
						</AdminButton>
						{data.status === "draft" ? (
							<AdminButton
								variant="primary"
								disabled={soumettre.isPending}
								onClick={() => soumettre.mutate()}
							>
								<AdminIcon n="check" s={14} />
								Envoyer en relecture
							</AdminButton>
						) : data.status !== "published" ? (
							<AdminButton
								variant="primary"
								disabled={publier.isPending}
								onClick={() => publier.mutate()}
							>
								<AdminIcon n="check" s={14} />
								Publier
							</AdminButton>
						) : null}
					</>
				)}
			</ModalFooter>
		</Modal>
	);
}
